// Polyfills must load before Shaka and any app code (see file headers). Keep
// them first.
import "@/lib/polyfills/mapUpsert";
import "@/lib/polyfills/arrayAt";
import "@/lib/polyfills/cryptoRandomUUID";

import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ApolloProvider } from "@apollo/client/react";
import { ErrorBoundary, captureException } from "@sentry/react";
import { apolloClient } from "./api/apiClient";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
import { initSentry } from "./lib/sentry";
import { exposeDevTools } from "./lib/devTools";
import { trackPageView } from "./lib/ga4";
import type { SeriesDetailsOrigin } from "@/types/amplitude-tracking.type";
import { initI18n } from "@/lib/i18n";
import { resolveBootLocale } from "@/lib/i18n/detectLocale";
import { parseSearch, stringifySearch } from "@/lib/searchParams";
import { useAppStore } from "@/stores/useApp";
import { installStaleChunkReload } from "@/lib/staleChunkReload";

exposeDevTools();
// Before any lazy import: the locale chunks load in `bootstrap()` below.
installStaleChunkReload();
// Create a new router instance

const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  // Lossless for numeric values the default JSON parsing would round.
  parseSearch,
  stringifySearch,
});

// Initialize Sentry before React renders. It sits after `createRouter` because
// the TanStack Router tracing integration needs the router instance at init
// (see lib/sentry.ts); `createRouter` is synchronous and the render happens
// later in `bootstrap()`, so this is still ahead of React.
initSentry(router);

// Emit GA4 page_view on initial load and on every SPA navigation. The GA4
// config in index.html sets send_page_view: false, so this is the only
// source of truth for both first paint and route changes. A hash-only
// navigation (My List's tabs) is not a new page: `page_path` excludes the
// hash, so the beacon would be a duplicate. On first load `fromLocation` is
// undefined, which reads as a path change.
router.subscribe("onResolved", ({ pathChanged, fromLocation, toLocation }) => {
  if (!pathChanged && fromLocation?.searchStr === toLocation.searchStr) return;
  trackPageView({
    page_location: window.location.href,
    page_title: document.title,
    page_path: window.location.pathname + window.location.search,
  });
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    // The `open series details` origin (SHO-757), carried by the deeplink
    // redirect and, later, by in-app links. Absent on a direct landing.
    seriesDetailsOrigin?: SeriesDetailsOrigin;
  }
}

// Sentry renders `fallback` with createElement, so this is a real component and
// hooks work here — no need to reach for the i18n singleton.
const BootErrorFallback = ({ resetError }: { resetError: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-2xl font-bold">{t("boot_error_title")}</h1>
      <p className="text-muted-foreground">{t("boot_error_message")}</p>
      <button
        onClick={resetError}
        className="bg-primary text-primary-foreground rounded px-4 py-2"
      >
        {t("boot_error_retry_button")}
      </button>
    </div>
  );
};

// Render the app once i18next has loaded the active locale (+ EN fallback).
const bootstrap = async () => {
  // The persisted locale is untrusted input; resolveBootLocale revalidates it.
  const initialLocale = resolveBootLocale(useAppStore.getState().locale);

  // i18n must never gate the render: this is the only path to root.render(), and
  // the ErrorBoundary below only covers what's already mounted — a rejection
  // here would leave #app empty for good.
  try {
    await initI18n(initialLocale);
  } catch (error) {
    captureException(error);
  }

  const rootElement = document.getElementById("app");
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <ErrorBoundary fallback={BootErrorFallback} showDialog={false}>
          <ApolloProvider client={apolloClient}>
            <RouterProvider router={router} />
          </ApolloProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  }
};

void bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
